/* ==========================================================================
   X-HEALTHY — discovery.js
   <brands-index>        live brand search + letter navigation state
   <wishlist-page>       renders saved products (browser storage)
   <compare-page>        side-by-side comparison table
   <protein-calculator>  daily protein needs
   ========================================================================== */
(() => {
  const { theme } = window;

  /* ---------- <brands-index> ---------- */
  if (!customElements.get('brands-index')) {
    customElements.define(
      'brands-index',
      class BrandsIndex extends HTMLElement {
        connectedCallback() {
          const used = JSON.parse(this.querySelector('[data-used-letters]')?.textContent || '[]');
          this.querySelectorAll('[data-letter-link]').forEach((link) => {
            if (!used.includes(link.dataset.letterLink)) {
              link.setAttribute('aria-disabled', 'true');
              link.tabIndex = -1;
            }
          });
          this.querySelector('[data-brand-search]')?.addEventListener('input', (event) => this.filter(event.target.value));
        }

        filter(value) {
          const term = value.trim().toLowerCase();
          let any = false;
          this.querySelectorAll('[data-letter]').forEach((group) => {
            let groupHas = false;
            group.querySelectorAll('[data-brand]').forEach((brand) => {
              const match = !term || brand.dataset.brand.includes(term);
              brand.parentElement.hidden = !match;
              if (match) groupHas = true;
            });
            group.hidden = !groupHas;
            if (groupHas) any = true;
          });
          this.querySelector('[data-brand-empty]').hidden = any;
        }
      }
    );
  }

  /* ---------- <wishlist-page> ---------- */
  if (!customElements.get('wishlist-page')) {
    customElements.define(
      'wishlist-page',
      class WishlistPage extends HTMLElement {
        connectedCallback() {
          this.grid = this.querySelector('[data-wishlist-grid]');
          this.summary = this.querySelector('[data-wishlist-summary]');
          this.empty = this.querySelector('[data-wishlist-empty]');
          this.actions = this.querySelector('[data-wishlist-actions]');
          this.querySelector('[data-wishlist-clear]')?.addEventListener('click', () => {
            theme.wishlist.all().forEach((handle) => theme.wishlist.toggle(handle));
          });
          this.querySelector('[data-wishlist-share]')?.addEventListener('click', () => this.share());

          // Shared link: ?items=handle1,handle2 imports into this browser
          const shared = new URLSearchParams(window.location.search).get('items');
          if (shared) {
            shared.split(',').filter(Boolean).forEach((handle) => {
              if (!theme.wishlist.has(handle)) theme.wishlist.toggle(handle);
            });
          }
          theme.events.on('wishlist:updated', () => this.render());
          this.render();
        }

        async render() {
          const handles = theme.wishlist.all();
          this.actions.hidden = handles.length === 0;
          if (!handles.length) {
            this.grid.replaceChildren();
            this.grid.hidden = true;
            this.empty.hidden = false;
            this.summary.textContent = theme.strings.wishlistEmpty;
            return;
          }
          this.empty.hidden = true;
          this.grid.hidden = false;
          // Re-use rendered cards; fetch only new ones
          const existing = new Map([...this.grid.querySelectorAll('product-card')].map((card) => [card.dataset.handle, card.closest('li')]));
          const missing = handles.filter((h) => !existing.has(h));
          const fetched = missing.length && theme.fetchCards ? await theme.fetchCards(missing) : [];
          fetched.forEach((card) => {
            const li = document.createElement('li');
            li.appendChild(card);
            existing.set(card.dataset.handle, li);
          });
          const items = handles.map((h) => existing.get(h)).filter(Boolean);
          this.grid.replaceChildren(...items);
          this.grid.removeAttribute('aria-busy');
          this.summary.textContent = theme.strings.wishlistCount.replace('[count]', items.length);
        }

        async share() {
          const url = new URL(window.location.href);
          url.search = '';
          url.searchParams.set('items', theme.wishlist.all().join(','));
          try {
            if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
              await navigator.share({ url: url.toString(), title: document.title });
            } else {
              await navigator.clipboard.writeText(url.toString());
              theme.toast({ message: theme.strings.linkCopied });
            }
          } catch {
            /* cancelled */
          }
        }
      }
    );
  }

  /* ---------- <compare-page> ---------- */
  if (!customElements.get('compare-page')) {
    customElements.define(
      'compare-page',
      class ComparePage extends HTMLElement {
        connectedCallback() {
          this.table = this.querySelector('[data-compare-table]');
          this.columns = this.querySelector('[data-compare-columns]');
          this.empty = this.querySelector('[data-compare-empty]');
          this.addEventListener('click', (event) => {
            const remove = event.target.closest('[data-compare-remove]');
            if (remove) theme.compare?.remove(remove.dataset.compareRemove);
          });
          this.addEventListener('submit', (event) => this.quickAdd(event));
          theme.events.on('compare:updated', () => this.render());
          // compare.js (the store) is a later deferred script; DOMContentLoaded fires after all of them
          if (theme.compare) this.render();
          else document.addEventListener('DOMContentLoaded', () => this.render(), { once: true });
        }

        async render() {
          const items = theme.compare ? theme.compare.all() : [];
          if (!items.length) {
            this.table.hidden = true;
            this.empty.hidden = false;
            return;
          }
          this.empty.hidden = true;
          const root = (theme.routes.root || '/').replace(/\/$/, '');
          const columns = await Promise.all(
            items.map(async (item) => {
              const existing = this.columns.querySelector(`[data-handle="${CSS.escape(item.handle)}"]`);
              if (existing) return existing;
              try {
                const res = await fetch(`${root}/products/${encodeURIComponent(item.handle)}?section_id=compare-item`);
                if (!res.ok) return null;
                const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
                return doc.querySelector('.compare-item');
              } catch {
                return null;
              }
            })
          );
          this.columns.replaceChildren(...columns.filter(Boolean));
          this.columns.style.setProperty('--count', this.columns.children.length);
          this.table.hidden = false;
          // Equalise row heights across columns
          requestAnimationFrame(() => this.alignRows());
        }

        alignRows() {
          const rows = [...this.querySelectorAll('.compare__label')].map((label) => label.dataset.row);
          rows.forEach((row) => {
            const cells = [...this.querySelectorAll(`[data-row="${row}"]`)];
            cells.forEach((cell) => (cell.style.minHeight = ''));
            const max = Math.max(...cells.map((cell) => cell.offsetHeight));
            cells.forEach((cell) => (cell.style.minHeight = `${max}px`));
          });
        }

        async quickAdd(event) {
          const form = event.target.closest('form[data-quick-add]');
          if (!form) return;
          event.preventDefault();
          const button = form.querySelector('[type="submit"]');
          button.setAttribute('aria-busy', 'true');
          try {
            await theme.cart.add(new FormData(form));
          } catch (error) {
            theme.toast({ message: error.message, type: 'error' });
          } finally {
            button.removeAttribute('aria-busy');
          }
        }
      }
    );
  }

  /* ---------- <protein-calculator> ---------- */
  if (!customElements.get('protein-calculator')) {
    customElements.define(
      'protein-calculator',
      class ProteinCalculator extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('[data-protein-form]');
          this.form.addEventListener('submit', (event) => {
            event.preventDefault();
            this.calculate();
          });
          this.form.addEventListener('change', () => {
            if (this.calculated) this.calculate();
          });
        }

        range(key) {
          const [min, max] = String(this.dataset[key] || '1-1.2').split('-').map(Number);
          return [min, max || min];
        }

        calculate() {
          const weight = Number(this.form.weight.value);
          const error = this.querySelector('[data-protein-error]');
          if (!weight || weight < 30 || weight > 250) {
            error.hidden = false;
            this.form.weight.focus();
            return;
          }
          error.hidden = true;
          this.calculated = true;

          let [min, max] = this.range(this.form.activity.value);
          const goal = this.form.goal.value;
          // Losing fat preserves muscle with more protein; building favours the upper end
          if (goal === 'lose') {
            min += 0.2;
            max += 0.2;
          } else if (goal === 'build') {
            min = Math.max(min, (min + max) / 2);
          }
          const low = Math.round(weight * min);
          const high = Math.round(weight * max);
          const scoop = Number(this.dataset.scoop) || 25;

          this.querySelector('[data-protein-range]').textContent = low === high ? `${low}` : `${low} – ${high}`;
          this.querySelector('[data-protein-meal]').textContent = `${Math.round(low / 4)} – ${Math.round(high / 4)} ${theme.strings.gramsShort}`;
          this.querySelector('[data-protein-scoops]').textContent = `${(low / scoop).toFixed(1)} – ${(high / scoop).toFixed(1)}`;
          this.querySelector('[data-protein-factor]').textContent = `${min.toFixed(1)} – ${max.toFixed(1)} ${theme.strings.gramsShort}`;
          this.querySelector('[data-protein-placeholder]').hidden = true;
          this.querySelector('[data-protein-output]').hidden = false;
          const products = this.querySelector('[data-protein-products]');
          if (products) products.hidden = false;
        }
      }
    );
  }
})();
